// Participant food-diary tab: a month calendar with per-day meal-logging status
// on the left, and the selected day's entries + add/edit controls inline on the
// right (master–detail). Data is loaded a visible-month at a time.
import React, { Suspense, lazy, useCallback, useRef, useState } from 'react'
import { Box, Stack, ResponsiveStack, Card, Text, Wrap, Alert, Spinner } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'
import FoodDiaryDayPanel from './FoodDiaryDayPanel'
import { MEALS, MEAL_COLORS, toDateStr } from './foodDiaryShared'

// The calendar pulls in FullCalendar (a large dependency), so load it lazily —
// it splits into its own chunk fetched only when the Food diaries tab is opened.
const FoodDiaryCalendar = lazy(() => import('./FoodDiaryCalendar'))

function MealLegend () {
  return (
    <Wrap gap={3} mt={3} px={1}>
      {MEALS.map(m => (
        <Stack key={m.key} direction='row' alignItems='center' gap={1}>
          <Box w='8px' h='8px' borderRadius='full' bg={MEAL_COLORS[m.key]} />
          <Text fontSize='xs' color='fg.muted'>{m.label}</Text>
        </Stack>
      ))}
    </Wrap>
  )
}

export default function FoodDiaries ({ trialId, participantId }) {
  const { trial } = useStore()
  const [entries, setEntries] = useState([])
  const [selectedDate, setSelectedDate] = useState(() => toDateStr(new Date()))
  const rangeRef = useRef(null)

  const load = useCallback(() => {
    const range = rangeRef.current
    if (!range) return
    api.foodDiaries.listEntries(trialId, participantId, range, res => {
      setEntries(res.foodDiaryEntries || [])
    }, err => utils.toast('error', err.message))
  }, [trialId, participantId])

  const onRangeChange = useCallback((from, to) => {
    rangeRef.current = { from, to }
    load()
  }, [load])

  if (!trial?.foodDiariesEnabled) {
    return (
      <Alert status='info'>
        Food diaries are not enabled for this trial. Enable them under Stages → Food diaries.
      </Alert>
    )
  }

  // meal-logging status per date, for the calendar markers
  const mealsByDate = {}
  entries.forEach(e => {
    if (!mealsByDate[e.date]) mealsByDate[e.date] = new Set()
    mealsByDate[e.date].add(e.meal)
  })

  const dayEntries = entries
    .filter(e => e.date === selectedDate)
    .sort((a, b) => (a.loggedAt || '').localeCompare(b.loggedAt || ''))

  return (
    <ResponsiveStack gap={5} alignItems='start'>
      <Box w={{ base: '100%', lg: '360px' }} flexShrink={0}>
        <Card variant='outline' p={3}>
          <Suspense fallback={<Box display='flex' justifyContent='center' py={10}><Spinner /></Box>}>
            <FoodDiaryCalendar
              mealsByDate={mealsByDate}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              onRangeChange={onRangeChange}
            />
          </Suspense>
          <MealLegend />
        </Card>
      </Box>
      <Box flex='1' minW={0} w='100%'>
        <FoodDiaryDayPanel
          trialId={trialId}
          participantId={participantId}
          date={selectedDate}
          entries={dayEntries}
          onChanged={load}
        />
      </Box>
    </ResponsiveStack>
  )
}
